import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import {
    BriefcaseBusiness,
    CalendarDays,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Clock,
    Coffee,
    Inbox,
    MapPin,
    XCircleIcon
} from "lucide-react";
import { noop } from "lodash";
import Spinner from "../../../../../../components/shared/Spinner";
import Avatar from "../../../../../../components/shared/Avatar";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../utils/api";
import { usePermissions } from "../../../hooks/usePermissions";
import { useUser } from "../../../../../../utils/hooks/user";
import { useCompanyTimeFormat } from "../../../../../../utils/hooks/company";
import { calculateSections } from "../../../hooks/form/useCalculatedSections";
import { renderSectionsDuration } from "../../../helpers/eventdisplay/renderSectionsDuration";
import { renderDuration } from "../../../helpers/eventdisplay/renderDuration";
import { renderUserName } from "../../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";
import { mapOpenShiftRequest } from "../../../helpers/data/mapOpenShiftRequest";
import { cn } from "../../../../../../lib/utils";

function OfferShifts({ approve, deny }: any) {
    const { t } = useTranslation();
    const userTimezone = moment.tz.guess();
    const companyTimeFormat = useCompanyTimeFormat();
    const [open, setOpen] = useState<any>({});
    const user = useUser();
    const { canApproveRequest, canDenyRequest } = usePermissions();
    const { type } = useScheduleContext();
    const [filters] = useFilters();
    const { authGet } = useApi();

    const { data, isLoading } = useQuery({
        queryKey: ["/request/my-team", {
            namespace: "schedule",
            pagination: "false",
            "filter[type][method]": "exact",
            "filter[type][value]": "offer-shifts",
            "filter[status][method]": "exact",
            "filter[status][value]": "pending",
            startDate: filters?.mode === "month" ? moment(filters?.from).startOf("week").format("YYYY-MM-DD") : filters?.from,
            endDate: filters?.mode === "month" ? moment(filters?.to).endOf("week").format("YYYY-MM-DD") : filters?.to,
        }],
        queryFn: ({ queryKey, signal }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params, signal });
        },
        enabled: ["home", "my-profile"].includes(type)
    });

    const offerShifts: any[] = data?.result || [];

    return isLoading ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-7">
            <Spinner className="tw-text-primary" />
        </div>
    ) : offerShifts?.length > 0 ? (
        <div>
            {offerShifts
                .map((shift: any) => mapOpenShiftRequest(shift, userTimezone))
                .map((shift: any) => {
                    const sections = calculateSections(shift?.sections || [], shift?.startDate);

                    const totalBreaksDuration = sections?.reduce((total: number, section: any) => {
                        if (section.type === "break" && section.start && section.end) {
                            const start = moment(section.start);
                            const end = moment(section.end);

                            if (start.isValid() && end.isValid()) {
                                const diff = end.diff(start, "seconds");
                                return total + (Number(diff) || 0);
                            }
                        }
                        return total;
                    }, 0);
                    const breaks = sections?.filter((section: any) => section?.type === "break");
                    const isApprover = (shift?.requiredToBeApprovedBy || []).includes(user?.id) || shift.requestType === "open-shift" || shift.requestType === "call-off";
                    const isPending = shift?.status === "pending" || shift?.status === "pending-canceled";

                    return (
                        <div className="tw-border tw-rounded-lg tw-p-3 tw-mb-2">
                            <div
                                className={cn(
                                    "tw-flex tw-items-center tw-justify-between",
                                    open?.[shift.id] && "tw-border-b tw-pb-3 tw-mb-3"
                                )}
                            >
                                <div className="tw-flex tw-items-center tw-text-sm tw-gap-2">
                                    <Avatar user={shift.user} size="sm" />
                                    <span>{renderUserName(shift.user)}</span>
                                </div>

                                <div className="tw-flex tw-gap-3 tw-items-center">
                                    {((canDenyRequest || isApprover) && isPending) && (
                                        <XCircleIcon
                                            className="tw-text-rose-500 tw-cursor-pointer"
                                            onClick={() => deny({ ...shift, close: noop })}
                                        />
                                    )}
                                    {((canApproveRequest || isApprover) && isPending) && (
                                        <CheckCircle2
                                            className="tw-text-primary tw-cursor-pointer"
                                            onClick={() => approve({ ...shift, close: noop })}
                                        />
                                    )}

                                    {open?.[shift.id] && (
                                        <ChevronUp
                                            className="tw-text-neutral-500 tw-cursor-pointer"
                                            size={19}
                                            onClick={() => setOpen((prev: any) => ({
                                                ...prev,
                                                [shift.id]: false
                                            }))}
                                        />
                                    )}
                                    {!open?.[shift.id] && (
                                        <ChevronDown
                                            className="tw-text-neutral-500 tw-cursor-pointer"
                                            size={19}
                                            onClick={() => setOpen((prev: any) => ({
                                                ...prev,
                                                [shift.id]: true
                                            }))}
                                        />
                                    )}
                                </div>
                            </div>

                            {open?.[shift.id] && (
                                <div className="">
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
                                        <CalendarDays size={19} className="tw-text-primary" />
                                        <span className="tw-text-sm">
                                            {moment.parseZone(sections?.[0]?.start).tz(userTimezone).format("ddd, DD MMMM YYYY")}
                                        </span>
                                    </div>

                                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                                        <div className="tw-flex tw-items-center tw-gap-3">
                                            <Clock size={19} className="tw-text-primary" />
                                            <span className="tw-text-sm">
                                                {moment.parseZone(sections?.[0]?.start).tz(userTimezone).format(companyTimeFormat)}
                                                {" - "}
                                                {moment.parseZone(sections?.[sections?.length - 1]?.end).tz(userTimezone).format(companyTimeFormat)}
                                            </span>
                                        </div>
                                        <div className="tw-bg-neutral-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-neutral-700 tw-font-semibold">
                                            {renderSectionsDuration(
                                                moment(sections?.[sections?.length - 1]?.end).diff(moment(sections?.[0]?.start), "seconds"),
                                                sections
                                            )}
                                        </div>
                                    </div>

                                    <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
                                        <div className="tw-flex tw-items-center tw-gap-3">
                                            <Coffee size={19} className="tw-text-primary" />
                                            <span className="tw-text-sm tw-flex tw-items-center tw-gap-1">
                                                {t("breaks")}
                                                <p className="tw-text-neutral-500">{`(${breaks?.length})`}</p>
                                            </span>
                                        </div>
                                        <div className="tw-bg-amber-100 tw-text-sm tw-px-3 tw-py-1 tw-rounded tw-text-amber-900 tw-font-semibold">
                                            {totalBreaksDuration > 0 ? renderDuration(totalBreaksDuration) : "0h"}
                                        </div>
                                    </div>

                                    {(sections?.[0]?.job || shift?.job) && (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                <BriefcaseBusiness size={19} className="tw-text-primary" />
                                                <span className="tw-text-sm">{t("job")}</span>
                                            </div>
                                            <div className="tw-bg-primary/20 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-primary tw-font-semibold">
                                                {renderSettingsResourceLabel(sections?.[0]?.jobs || shift?.job)}
                                            </div>
                                        </div>
                                    )}

                                    {(sections?.[0]?.location || shift?.location) && (
                                        <div className="tw-flex tw-items-center tw-justify-between">
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                <MapPin size={19} className="tw-text-primary" />
                                                <span className="tw-text-sm">{t("location")}</span>
                                            </div>
                                            <div className="tw-bg-purple-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-purple-950 tw-font-semibold">
                                                {renderSettingsResourceLabel(sections?.[0]?.location || shift?.location)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
        </div>
    ) : (
        <div className="tw-flex tw-flex-col tw-items-center tw-text-neutral-500 tw-border tw-rounded-sm tw-py-7">
            <Inbox />
            <span>{t("no-data")}</span>
        </div>
    );
}

export default OfferShifts;
